import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { createSelectColumn, SortableHeader } from '~/components/ui/data-table';
import { formatCurrency, formatDate } from '~/utils/paymentUtils';
import { deleteDisabledReason, selectable, voidDisabledReason, type SchoolInvoiceTableRow } from './invoiceRules';

function statusVariant(displayStatus: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (displayStatus === 'Paid') return 'default';
  if (displayStatus === 'Payment Failed') return 'destructive';
  if (displayStatus === 'Payment Processing' || displayStatus.startsWith('Charges') || displayStatus.startsWith('Sending')) {
    return 'secondary';
  }
  return 'outline';
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

// Tuition requires auto-pay, unless the office waived the family's auto-pay
// step on that year's checklist and the tuition isn't charged automatically
// after all (the family's auto-pay can still cover the year).
const requiresAutoPayWord = (row: SchoolInvoiceTableRow) =>
  row.requiresAutoPay && row.autoPayWaived && !row.autoCollectable ? 'Waived' : yesNo(row.requiresAutoPay);

interface ColumnHandlers {
  schoolSlug: string | undefined;
  onVoid: (row: SchoolInvoiceTableRow) => void;
  onDelete: (row: SchoolInvoiceTableRow) => void;
}

export function getSchoolInvoiceColumns({ schoolSlug, onVoid, onDelete }: ColumnHandlers): ColumnDef<SchoolInvoiceTableRow>[] {
  const billingPath = (row: SchoolInvoiceTableRow) => `/app/${schoolSlug}/families/${row.familySlug}/billing`;

  return [
    createSelectColumn<SchoolInvoiceTableRow>({
      // A draft is always selectable (it can be edited or deleted even when it
      // cannot be posted), so the title only ever explains a non-draft.
      disabledTitle: (row) => (selectable(row) ? null : row.postDisabledReason),
    }),
    {
      // The search box reads this column, and `searchText` carries the family
      // name and the memo so one box finds either.
      id: 'family',
      accessorKey: 'searchText',
      header: ({ column }) => <SortableHeader column={column}>Family</SortableHeader>,
      meta: { sortKey: (row: SchoolInvoiceTableRow) => row.familyName },
      cell: ({ row }) => (
        <Link to={billingPath(row.original)} className="font-medium hover:underline" data-no-row-click>
          {row.original.familyName}
        </Link>
      ),
    },
    {
      accessorKey: 'memo',
      header: 'Description',
      cell: ({ row }) => <span>{row.original.memo ?? '—'}</span>,
    },
    {
      accessorKey: 'dueDate',
      header: ({ column }) => <SortableHeader column={column}>Due date</SortableHeader>,
      meta: { sortKey: (row: SchoolInvoiceTableRow) => row.dueDate ?? null },
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{row.original.dueDate ? formatDate(row.original.dueDate) : '—'}</span>
      ),
    },
    {
      accessorKey: 'totalCents',
      header: ({ column }) => <SortableHeader column={column}>Amount</SortableHeader>,
      meta: { sortKey: (row: SchoolInvoiceTableRow) => row.totalCents },
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          <div>{formatCurrency(row.original.totalCents)}</div>
          {row.original.feeCents > 0 && (
            <div className="text-xs text-muted-foreground">incl. {formatCurrency(row.original.feeCents)} card fee</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'displayStatus',
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      meta: {
        sortKey: (row: SchoolInvoiceTableRow) => row.displayStatus,
        facet: { title: 'Status', getKey: (row: SchoolInvoiceTableRow) => row.displayStatus },
      },
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.displayStatus)} className="whitespace-nowrap">
          {row.original.displayStatus}
        </Badge>
      ),
    },
    {
      accessorKey: 'collection',
      header: "How it's paid",
      meta: { facet: { title: "How it's paid", getKey: (row: SchoolInvoiceTableRow) => row.collection } },
      cell: ({ row }) => <span className="whitespace-nowrap">{row.original.collection}</span>,
    },
    {
      accessorKey: 'paymentMethod',
      header: 'Payment method on file',
      meta: {
        facet: {
          title: 'Payment method on file',
          getKey: (row: SchoolInvoiceTableRow) => yesNo(row.paymentMethodOnFile),
        },
      },
      cell: ({ row }) => (
        <span className={row.original.paymentMethodOnFile ? 'whitespace-nowrap' : 'text-muted-foreground'}>
          {row.original.paymentMethod}
        </span>
      ),
    },
    {
      id: 'requiresAutoPay',
      accessorFn: requiresAutoPayWord,
      header: 'Requires auto-pay',
      meta: {
        facet: { title: 'Requires auto-pay', getKey: requiresAutoPayWord },
      },
      cell: ({ row }) => <span>{requiresAutoPayWord(row.original)}</span>,
    },
    {
      id: 'note',
      header: '',
      enableSorting: false,
      cell: ({ row }) =>
        !row.original.postable && row.original.postDisabledReason ? (
          <span className="text-xs text-muted-foreground">{row.original.postDisabledReason}</span>
        ) : null,
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const invoice = row.original;
        const voidReason = voidDisabledReason(invoice);
        const deleteReason = deleteDisabledReason(invoice);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0" aria-label={`Actions for ${invoice.memo ?? invoice.familyName}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to={billingPath(invoice)}>Open family billing</Link>
              </DropdownMenuItem>
              {invoice.stripeHostedInvoiceUrl && (
                <DropdownMenuItem asChild>
                  <a href={invoice.stripeHostedInvoiceUrl} target="_blank" rel="noreferrer">
                    Open in Stripe
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!!deleteReason}
                title={deleteReason ?? undefined}
                onClick={() => onDelete(invoice)}
                className="text-destructive focus:text-destructive"
              >
                Delete draft
              </DropdownMenuItem>
              {deleteReason && (
                <div className="px-2 pb-1.5 text-xs text-muted-foreground">{deleteReason}</div>
              )}
              <DropdownMenuItem
                disabled={!!voidReason}
                title={voidReason ?? undefined}
                onClick={() => onVoid(invoice)}
                className="text-destructive focus:text-destructive"
              >
                Void invoice
              </DropdownMenuItem>
              {voidReason && (
                <div className="px-2 pb-1.5 text-xs text-muted-foreground">{voidReason}</div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}
